import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  IconButton,
  Collapse,
  Chip,
  Button,
  Dialog,
  TableSortLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Chat as ChatIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { API_URL } from '../../lib/constants';
import { useUserInfoContext } from '../../lib/contexts/UserInfoContext';
import { FeedbackEntry, FeedbackSummary, feedbackCategoryLabel } from '@common/domain/types';
import type { FeedbackMode } from '@common/domain/types';
import { canAccessPersona, isSuperAdmin } from '@common/domain/auth';
import { useChat } from '../../lib/hooks/UseChat';
import { GetSessionRes } from '../../lib/models/Chat';
import { Exchange } from '../../lib/interfaces/Chat';
import { MarkdownContent } from '../../components/Chat/MarkdownContent';
import { ClassificationBadge, SafetyRoute } from '../../components/ClassificationBadge/ClassificationBadge';
import { WebSearchBadge } from '../../components/Chat/WebSearchBadge';

// Mirror of the helper in TextChatPage so the feedback modal matches the real chat.
const isWebSearchResponseMode = (mode: string | undefined | null): boolean => {
  if (mode == null || typeof mode !== 'string') return false;
  return mode.trim().toLowerCase().replace(/_/g, '') === 'websearch';
};

// Exchanges in the conversation history carry safetyRoute / responseMode either as
// direct fields or nested inside the metadata JSON string. Mirrors UseChat.getChatMessages.
const parseExchangeMeta = (exchange: Exchange): { safetyRoute?: SafetyRoute; responseMode?: string } => {
  let safetyRoute = exchange.safetyRoute;
  let responseMode: string | undefined;
  const metadata = exchange.metadata;
  let queryMetadata: Record<string, unknown> = {};
  if (typeof metadata === 'string' && metadata.trim()) {
    try {
      queryMetadata = JSON.parse(metadata) as Record<string, unknown>;
    } catch {
      queryMetadata = {};
    }
  } else if (metadata && typeof metadata === 'object') {
    queryMetadata = metadata as Record<string, unknown>;
  }
  if (!safetyRoute && queryMetadata.safetyRoute) {
    safetyRoute = queryMetadata.safetyRoute as SafetyRoute;
  }
  if (typeof queryMetadata.responseMode === 'string') {
    responseMode = queryMetadata.responseMode;
  }
  return { safetyRoute, responseMode };
};

const BUCKETS = [
  { id: 1, name: 'Greeting Response Quality',       priority: 'P2' },
  { id: 2, name: 'Response Length & Truncation',    priority: 'P2' },
  { id: 3, name: 'Citations & Reference Links',     priority: 'P1' },
  { id: 4, name: 'Factual & Doctrinal Inaccuracy',  priority: 'P0' },
  { id: 5, name: 'AI Identity & Safety Guardrails', priority: 'P0' },
  { id: 6, name: 'Safety Filter Over-Sensitivity',  priority: 'P1' },
  { id: 7, name: 'Context Tracking',                priority: 'P2' },
  { id: 8, name: 'System / Technical Errors',       priority: 'P1' },
  { id: 9, name: 'Noise / Testing Artifacts',       priority: '—'  },
] as const;

const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  P0: 'error',
  P1: 'warning',
  P2: 'default',
  '—': 'default',
};

function classifyEntry(entry: FeedbackEntry): number {
  const q = (entry.question || '').toLowerCase().trim();
  const a = (entry.answer   || '').toLowerCase();
  const c = (entry.comment  || '').toLowerCase();
  const rawC = (entry.comment || '').trim();

  const noiseWords = new Set(['hiii','hi','false','no','bad answer','solution needed',
    'better response expected','elaborate more']);
  if (!q && !a && noiseWords.has(rawC.toLowerCase())) return 9;

  if (c.includes('timeout') || c.includes('error message') || c.includes('backend') ||
      a.includes("i'm having trouble accessing my knowledge")) return 8;

  if (a.includes('i am not able to help with that request') ||
      (c.includes('in danger') && c.includes('not'))) return 6;

  if (c.includes('guardrail') || c.includes('disclaimer') ||
      (c.includes('concern') && ['safety','ethic','moral'].some(w => c.includes(w)))) return 5;

  if (['not tracking','football','basketball','lost context'].some(w => c.includes(w))) return 7;

  if (['backwards','wrong','incorrect','inaccurate','fabricated','controversial',
       '2025','nfl is over','president','passion translation','pillars','realistic']
      .some(w => c.includes(w))) return 4;

  if (['citation','link','source','reference','cite'].some(w => c.includes(w))) return 3;

  if (['truncat','cut off','too short','elaborate','longer bio','more elaborat','longer']
      .some(w => c.includes(w))) return 2;

  if (q === 'hello' || (q.length <= 10 && c.includes('not helpful'))) return 1;

  return 9;
}

const FeedbackTab = () => {
  const { userInfo } = useUserInfoContext();
  const { getChatSession } = useChat();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{ summary: FeedbackSummary, entries: FeedbackEntry[] } | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [personaId, setPersonaId] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState<FeedbackMode | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [personas, setPersonas] = useState<{ id: string, name: string, category?: string, tenantId?: string }[]>([]);
  const [bucketFilter, setBucketFilter] = useState<number | null>(null);
  const [bucketSortCol, setBucketSortCol] = useState<'id' | 'name' | 'count' | 'priority'>('count');
  const [bucketSortDir, setBucketSortDir] = useState<'asc' | 'desc'>('desc');
  const [betaOnly, setBetaOnly] = useState(true);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<GetSessionRes | null>(null);
  const [targetExchangeId, setTargetExchangeId] = useState<string | null>(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);

  useEffect(() => {
    if (modalOpen && !modalLoading && targetExchangeId) {
      setTimeout(() => {
        const element = document.getElementById(`exchange-${targetExchangeId}-assistant`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [modalOpen, modalLoading, targetExchangeId]);

  useEffect(() => {
    fetchPersonas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaId, ratingFilter, modeFilter, dateRange, customStartDate, customEndDate, categoryFilter, tenantFilter, betaOnly, userFilter]);

  const fetchPersonas = async () => {
    if (!userInfo?.accessToken) return;
    try {
      const response = await fetch(`${API_URL}/personas`, {
        headers: { Authorization: `Bearer ${userInfo.accessToken}` }
      });
      const result = await response.json();
      const isSuper = isSuperAdmin(userInfo?.groups);
      const filtered = Array.isArray(result)
        ? (isSuper ? result : result.filter(p => canAccessPersona(p, userInfo)))
        : [];
      setPersonas(filtered);
    } catch (err) {
      console.error('Failed to fetch personas', err);
    }
  };


  const fetchFeedback = async () => {
    setBucketFilter(null);
    setLoading(true);
    try {
      let url = `${API_URL}/admin/feedback?rating=${ratingFilter}&limit=200`;
      if (modeFilter !== 'all') url += `&mode=${modeFilter}`;
      if (personaId !== 'all') url += `&personaId=${personaId}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      if (tenantFilter !== 'all') url += `&tenantId=${tenantFilter}`;
      if (betaOnly) url += `&betaOnly=true`;
      if (userFilter !== 'all') url += `&user=${encodeURIComponent(userFilter)}`;

      const presetDays: Record<string, number> = { '7days': 7, '30days': 30, '90days': 90 };
      if (dateRange in presetDays) {
        const start = new Date();
        start.setDate(start.getDate() - presetDays[dateRange]);
        url += `&startDate=${start.toISOString()}&endDate=${new Date().toISOString()}`;
      } else if (customStartDate && customEndDate) {
        url += `&startDate=${new Date(customStartDate).toISOString()}&endDate=${new Date(customEndDate).toISOString()}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${userInfo?.accessToken}` }
      });
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Failed to fetch feedback', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIssue = async (exchangeId: string) => {
  const response = await fetch(`${API_URL}/admin/feedback/${exchangeId}/github-issue`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${userInfo?.accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to create GitHub issue');
  }

  const result = await response.json();

  setData((prev) => {
    if (!prev) return prev;

    return {
      ...prev,
      entries: prev.entries.map((entry) =>
        entry.exchangeId === exchangeId
          ? {
              ...entry,
              githubIssueUrl: result.issueUrl,
              githubIssueNumber: result.issueNumber,
            }
          : entry
      ),
    };
  });

  return result;
};
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const uniqueUsers = useMemo(() => {
    if (!data?.entries) return [];
    const users = data.entries.map(e => e.userEmail || e.userId).filter(Boolean);
    return [...new Set(users)].sort((a, b) => a!.localeCompare(b!));
  }, [data?.entries]);

  const uniqueCategories = useMemo(() => {
    const cats = new Set<string>();
    personas.forEach(p => { if (p.category) cats.add(p.category); });
    data?.entries?.forEach(e => { if (e.category) cats.add(e.category); });
    return [...cats].sort((a, b) => a.localeCompare(b));
  }, [personas, data?.entries]);

  const uniqueTenants = useMemo(() => {
    const tens = new Set<string>();
    personas.forEach(p => { if (p.tenantId) tens.add(p.tenantId); });
    data?.entries?.forEach(e => { if (e.tenantId) tens.add(e.tenantId); });
    return [...tens].sort((a, b) => a.localeCompare(b));
  }, [personas, data?.entries]);

  const filteredPersonas = useMemo(() => {
    let result = personas;
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    if (tenantFilter !== 'all') {
      result = result.filter(p => p.tenantId === tenantFilter);
    }
    return result;
  }, [personas, categoryFilter, tenantFilter]);

  useEffect(() => {
    if (personaId !== 'all' && !filteredPersonas.some(p => p.id === personaId)) {
      setPersonaId('all');
    }
  }, [filteredPersonas, personaId]);

  const baseFilteredEntries = useMemo(() => {
    return data?.entries || [];
  }, [data?.entries]);

  const filteredEntries = useMemo(() => {
    let entries = baseFilteredEntries;
    if (bucketFilter !== null) {
      entries = entries.filter(e => parseInt(e.rating) < 0 && classifyEntry(e) === bucketFilter);
    }
    return entries;
  }, [baseFilteredEntries, bucketFilter]);

  const bucketMap = useMemo(() => {
    const map: Record<number, FeedbackEntry[]> = {};
    BUCKETS.forEach(b => { map[b.id] = []; });
    if (!data?.entries) return map;

    const entries = data.entries.filter(e => parseInt(e.rating) < 0);
    entries.forEach(e => {
      const id = classifyEntry(e);
      map[id]?.push(e);
    });
    return map;
  }, [data?.entries]);

  const dynamicSummary = useMemo(() => {
    return data?.summary || {
      totalPositive: 0,
      totalNegative: 0,
      personaStats: {} as Record<string, { positive: number, negative: number, personaName?: string }>,
    };
  }, [data?.summary]);

  const sortedBuckets = useMemo(() => {
    const PRIORITY_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, '—': 3 };
    return [...BUCKETS].sort((a, b) => {
      let cmp = 0;
      if (bucketSortCol === 'id')       cmp = a.id - b.id;
      else if (bucketSortCol === 'name')     cmp = a.name.localeCompare(b.name);
      else if (bucketSortCol === 'count')    cmp = (bucketMap[a.id]?.length ?? 0) - (bucketMap[b.id]?.length ?? 0);
      else if (bucketSortCol === 'priority') cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      return bucketSortDir === 'asc' ? cmp : -cmp;
    });
  }, [bucketSortCol, bucketSortDir, bucketMap]);

  const handleBucketSort = (col: typeof bucketSortCol) => {
    if (bucketSortCol === col) {
      setBucketSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setBucketSortCol(col);
      setBucketSortDir('asc');
    }
  };

  const handleViewConversation = async (entry: FeedbackEntry) => {
    setTargetExchangeId(entry.exchangeId);
    setSelectedPersonaId(entry.personaId || null);
    setSelectedSession(null);
    setModalError(null);
    setModalOpen(true);

    if (!userInfo) return;

    if (!entry.chatId) {
      setModalError('No chat ID stored on this feedback entry (likely a legacy record).');
      return;
    }
    if (!entry.userId || entry.userId === 'anonymous') {
      setModalError('No user ID stored on this feedback entry (likely a legacy record).');
      return;
    }

    setModalLoading(true);
    try {
      const session = await getChatSession(userInfo, entry.chatId, entry.userId);
      if (session) {
        setSelectedSession(session);
      } else {
        setModalError('Conversation not found. The session may have expired or the chat ID may be invalid.');
      }
    } catch (err: any) {
      console.error('Failed to fetch conversation session', err);
      const status = err?.response?.status;
      if (status === 403) {
        setModalError('Access denied. Your account may not have permission to view this conversation.');
      } else if (status === 404) {
        setModalError('Conversation not found. The session may have expired.');
      } else {
        setModalError(`Failed to load conversation: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setModalLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
      {loading && data && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1100 }}>
          <LinearProgress />
        </Box>
      )}
      <Typography variant="h4" gutterBottom>User Feedback Dashboard</Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select value={userFilter} label="User" onChange={(e) => { setUserFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Users</MenuItem>
                {uniqueUsers.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Persona Category</InputLabel>
              <Select value={categoryFilter} label="Persona Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Persona Categories</MenuItem>
                {uniqueCategories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Tenant ID</InputLabel>
              <Select value={tenantFilter} label="Tenant ID" onChange={(e) => { setTenantFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Tenants</MenuItem>
                {uniqueTenants.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Persona</InputLabel>
              <Select value={personaId} label="Persona" onChange={(e) => { setPersonaId(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Personas</MenuItem>
                {[...filteredPersonas].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Rating</InputLabel>
              <Select value={ratingFilter} label="Rating" onChange={(e) => { setRatingFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="negative">Negative Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Surface</InputLabel>
              <Select
                value={modeFilter}
                label="Surface"
                onChange={(e) => { setModeFilter(e.target.value as FeedbackMode | 'all'); setPage(0); }}
              >
                <MenuItem value="all">All Surfaces</MenuItem>
                <MenuItem value="AVATAR">Avatar</MenuItem>
                <MenuItem value="TEXT">Chatbot</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Date Range</InputLabel>
              <Select value={dateRange} label="Date Range" onChange={(e) => { setDateRange(e.target.value); setPage(0); }}>
                <MenuItem value="7days">Last 7 Days</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="90days">Last 90 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {dateRange === 'custom' && (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'success.light' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary' }}>Total Positive</Typography>
              <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {dynamicSummary.totalPositive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'error.light' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary' }}>Total Negative</Typography>
              <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {dynamicSummary.totalNegative}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: 'info.light' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'text.primary' }}>Overall Positive %</Typography>
              <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700 }}>
                {Math.round((dynamicSummary.totalPositive / (dynamicSummary.totalPositive + dynamicSummary.totalNegative || 1)) * 100)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {data?.summary.categoryStats && Object.keys(data.summary.categoryStats).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Top Failure Reasons</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(data.summary.categoryStats)
              .sort(([, left], [, right]) => right - left)
              .map(([category, count]) => (
                <Chip
                  key={category}
                  label={`${feedbackCategoryLabel(category)}: ${count}`}
                  variant="outlined"
                  color="error"
                  size="small"
                />
              ))}
          </Box>
        </Paper>
      )}

      {/* Surface Stats — avatar and chatbot feedback share one store; this is the split. */}
      <Typography variant="h6" gutterBottom>Stats by Surface</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Surface</TableCell>
              <TableCell align="right">Positive</TableCell>
              <TableCell align="right">Negative</TableCell>
              <TableCell align="right">Positive %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(['AVATAR', 'TEXT'] as const).map(m => {
              const stats = data?.summary.modeStats?.[m] || { positive: 0, negative: 0 };
              const total = stats.positive + stats.negative;
              return (
                <TableRow key={m}>
                  <TableCell>{m === 'AVATAR' ? 'Avatar' : 'Chatbot'}</TableCell>
                  <TableCell align="right">{stats.positive}</TableCell>
                  <TableCell align="right">{stats.negative}</TableCell>
                  <TableCell align="right">
                    {total ? Math.round((stats.positive / total) * 100) : 0}%
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Persona Stats Table */}
      <Typography variant="h6" gutterBottom>Stats by Persona</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Persona</TableCell>
              <TableCell align="right">Positive</TableCell>
              <TableCell align="right">Negative</TableCell>
              <TableCell align="right">Positive %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(dynamicSummary.personaStats).map(([pId, stats]: [string, any]) => (
              <TableRow key={pId}>
                <TableCell>{stats.personaName || pId}</TableCell>
                <TableCell align="right">{stats.positive}</TableCell>
                <TableCell align="right">{stats.negative}</TableCell>
                <TableCell align="right">
                  {Math.round((stats.positive / (stats.positive + stats.negative || 1)) * 100)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Negative Feedback Categories */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h6">Negative Feedback by Category</Typography>
        <Chip
          label={betaOnly ? 'Beta users only' : 'All users'}
          size="small"
          color={betaOnly ? 'primary' : 'default'}
          onClick={() => { setBucketFilter(null); setBetaOnly(v => !v); setPage(0); }}
          sx={{ cursor: 'pointer' }}
        />
        {bucketFilter !== null && (
          <Chip
            label={`Showing: ${BUCKETS.find(b => b.id === bucketFilter)?.name}`}
            size="small"
            onDelete={() => { setBucketFilter(null); setPage(0); }}
          />
        )}
      </Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={bucketSortCol === 'id' ? bucketSortDir : false}>
                <TableSortLabel active={bucketSortCol === 'id'} direction={bucketSortCol === 'id' ? bucketSortDir : 'asc'} onClick={() => handleBucketSort('id')}>#</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={bucketSortCol === 'name' ? bucketSortDir : false}>
                <TableSortLabel active={bucketSortCol === 'name'} direction={bucketSortCol === 'name' ? bucketSortDir : 'asc'} onClick={() => handleBucketSort('name')}>Category</TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={bucketSortCol === 'count' ? bucketSortDir : false}>
                <TableSortLabel active={bucketSortCol === 'count'} direction={bucketSortCol === 'count' ? bucketSortDir : 'asc'} onClick={() => handleBucketSort('count')}>Count</TableSortLabel>
              </TableCell>
              <TableCell sortDirection={bucketSortCol === 'priority' ? bucketSortDir : false}>
                <TableSortLabel active={bucketSortCol === 'priority'} direction={bucketSortCol === 'priority' ? bucketSortDir : 'asc'} onClick={() => handleBucketSort('priority')}>Priority</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedBuckets.map(bucket => {
              const count = bucketMap[bucket.id]?.length ?? 0;
              const isSelected = bucketFilter === bucket.id;
              return (
                <TableRow
                  key={bucket.id}
                  hover
                  selected={isSelected}
                  onClick={() => { setBucketFilter(isSelected ? null : bucket.id); setPage(0); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{bucket.id}</TableCell>
                  <TableCell>{bucket.name}</TableCell>
                  <TableCell align="right">
                    <Chip label={count} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={bucket.priority}
                      size="small"
                      color={PRIORITY_COLOR[bucket.priority]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detailed Table */}
      <Typography variant="h6" gutterBottom>Feedback Details</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Persona</TableCell>
              <TableCell>Surface</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Reported</TableCell>
              <TableCell align="right">Turn&nbsp;(s)</TableCell>
              <TableCell>Comment</TableCell>
              <TableCell>Issue</TableCell>
              <TableCell>Conv ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEntries
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((entry, index) => (
                <Row
                  key={`${entry.exchangeId}-${entry.timestamp}-${index}`}
                  entry={entry}
                  onViewConversation={handleViewConversation}
                  onCreateIssue={handleCreateIssue}
                />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEntries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Conversation Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Conversation History
        </DialogTitle>
        <DialogContent dividers>
          {modalLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : modalError ? (
            <Box p={2}>
              <Typography color="error" variant="body2">{modalError}</Typography>
            </Box>
          ) : selectedSession?.conversation ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedSession.conversation.map((exchange: Exchange) => {
                const isTarget = exchange.exchangeId === targetExchangeId || (exchange as any).exchange_id === targetExchangeId;
                const { safetyRoute, responseMode } = parseExchangeMeta(exchange);
                return (
                  <React.Fragment key={exchange.exchangeId || (exchange as any).exchange_id}>
                    {/* User Message */}
                    <Box
                      id={isTarget ? `exchange-${exchange.exchangeId || (exchange as any).exchange_id}-user` : undefined}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        alignSelf: 'flex-end',
                        maxWidth: '85%',
                        border: isTarget ? '2px solid' : 'none',
                        borderColor: 'primary.main',
                        boxShadow: isTarget ? 3 : 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        User
                      </Typography>
                      <Typography variant="body2">
                        {exchange.question}
                      </Typography>
                    </Box>

                    {/* Classification Badges — mirrors the real chat for debugging context */}
                    {(safetyRoute || isWebSearchResponseMode(responseMode)) && (
                      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {safetyRoute && <ClassificationBadge safetyRoute={safetyRoute} compact />}
                        {isWebSearchResponseMode(responseMode) && (
                          <div className="classification-badge-container compact">
                            <WebSearchBadge />
                          </div>
                        )}
                      </Box>
                    )}

                    {/* Assistant Message */}
                    <Box
                      id={isTarget ? `exchange-${exchange.exchangeId || (exchange as any).exchange_id}-assistant` : undefined}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        alignSelf: 'flex-start',
                        maxWidth: '85%',
                        border: isTarget ? '2px solid' : 'none',
                        borderColor: 'secondary.main',
                        boxShadow: isTarget ? 3 : 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {personas.find(p => p.id === selectedPersonaId)?.name || selectedPersonaId || 'Assistant'}
                      </Typography>
                      <MarkdownContent content={exchange.response} sources={exchange.sources} />
                      {isTarget && (
                        <Box mt={1}>
                          <Chip label="Target Exchange" color="secondary" size="small" />
                        </Box>
                      )}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Typography>No conversation history found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Row = ({
  entry,
  onViewConversation,
  onCreateIssue,
}: {
  entry: FeedbackEntry;
  onViewConversation: (entry: FeedbackEntry) => void;
  onCreateIssue: (exchangeId: string) => Promise<{ issueUrl?: string; issueNumber?: number }>;
}) => {
  const [open, setOpen] = useState(false);
  const [isCreatingIssue, setIsCreatingIssue] = useState(false);
  const [issueUrl, setIssueUrl] = useState<string | undefined>(entry.githubIssueUrl);
  const isPositive = parseInt(entry.rating) > 0;
  const totalTurnMs = entry.turnMetrics?.totalTurnMs;
  const latencyBreakdown = useMemo(() => {
    const m = entry.turnMetrics;
    if (!m) return '';
    const parts: string[] = [];
    if (m.transcriptionMs !== undefined) parts.push(`ASR ${m.transcriptionMs}ms`);
    if (m.llmFirstTokenMs !== undefined) parts.push(`LLM first token ${m.llmFirstTokenMs}ms`);
    if (m.ttsFirstByteMs !== undefined) parts.push(`TTS first byte ${m.ttsFirstByteMs}ms`);
    if (m.firstAudioChunkMs !== undefined) parts.push(`first audio ${m.firstAudioChunkMs}ms`);
    if (m.totalTurnMs !== undefined) parts.push(`total ${m.totalTurnMs}ms`);
    return parts.join(' · ');
  }, [entry.turnMetrics]);

  const handleCreateIssueClick = async () => {
    if (issueUrl || isCreatingIssue) return;

    try {
      setIsCreatingIssue(true);
      console.log("exchangeId", entry.exchangeId);
      const result = await onCreateIssue(entry.exchangeId);
      if (result?.issueUrl) {
        setIssueUrl(result.issueUrl);
      }
    } catch (err) {
      console.error('Failed to create issue', err);
    } finally {
      setIsCreatingIssue(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
      >
        <TableCell>
          <IconButton size="small" onClick={e => { e.stopPropagation(); setOpen(!open); }}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{new Date(entry.timestamp).toLocaleString()}</TableCell>
        <TableCell sx={{ wordBreak: 'break-word', maxWidth: '200px' }}>
          {entry.userEmail || entry.userId}
        </TableCell>
        <TableCell>{entry.personaName}</TableCell>
        <TableCell>
          <Chip
            label={entry.feedbackMode === 'AVATAR' ? 'Avatar' : 'Chatbot'}
            color={entry.feedbackMode === 'AVATAR' ? 'secondary' : 'default'}
            variant="outlined"
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={isPositive ? 'Positive' : 'Negative'}
            color={isPositive ? 'success' : 'error'}
            size="small"
          />
        </TableCell>
        <TableCell sx={{ maxWidth: 220 }}>
          {entry.feedbackCategory && entry.feedbackCategory.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {entry.feedbackCategory.map(cat => (
                <Chip key={cat} label={feedbackCategoryLabel(cat)} size="small" variant="outlined" />
              ))}
            </Box>
          ) : (
            <span style={{ color: 'var(--mui-palette-text-disabled, #888)' }}>—</span>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {totalTurnMs !== undefined ? (
            <Tooltip title={latencyBreakdown}>
              <span style={{ color: totalTurnMs > 5000 ? 'var(--mui-palette-error-main, #d32f2f)' : undefined }}>
                {(totalTurnMs / 1000).toFixed(1)}
              </span>
            </Tooltip>
          ) : (
            <span style={{ color: 'var(--mui-palette-text-disabled, #888)' }}>—</span>
          )}
        </TableCell>
        <TableCell>{entry.comment}</TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          {issueUrl ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => window.open(issueUrl, '_blank', 'noopener,noreferrer')}
            >
              View Issue
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={handleCreateIssueClick}
              disabled={isCreatingIssue}
            >
              {isCreatingIssue ? <CircularProgress size={16} /> : 'Create Issue'}
            </Button>
          )}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
          {entry.chatId ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={entry.chatId}>
                <span>{entry.chatId.length > 10 ? `${entry.chatId.slice(0, 8)}…` : entry.chatId}</span>
              </Tooltip>
              <Tooltip title="Copy">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard?.writeText(entry.chatId);
                  }}
                  aria-label="Copy conversation id"
                >
                  <ContentCopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <span style={{ color: 'var(--mui-palette-text-disabled, #888)' }}>—</span>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip label={`Tenant: ${entry.tenantId || 'default'}`} size="small" variant="outlined" color="primary" />
                  <Chip label={`Category: ${entry.category || 'unknown'}`} size="small" variant="outlined" color="secondary" />
                </Box>
                {latencyBreakdown && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Turn timing:</strong> {latencyBreakdown}
                  </Typography>
                )}
                {entry.clientContext && (
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }} gutterBottom>
                    <strong>Client:</strong>{' '}
                    {Object.entries(entry.clientContext)
                      .filter(([, value]) => value)
                      .map(([name, value]) => `${name}: ${value}`)
                      .join(' · ') || 'Not recorded'}
                  </Typography>
                )}
                <Typography variant="subtitle2" color="primary">Question:</Typography>
                <Typography variant="body2" gutterBottom>{entry.question}</Typography>
                <Typography variant="subtitle2" color="secondary">Answer:</Typography>
                <Typography variant="body2">{entry.answer}</Typography>
              </Box>
              <Box sx={{ ml: 2, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ChatIcon />}
                  onClick={() => onViewConversation(entry)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  View Full Conversation
                </Button>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default FeedbackTab;
